import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "./AuthContext";

import en from "../locales/en";
import hi from "../locales/hi";
import te from "../locales/te";
import ur from "../locales/ur";
import ta from "../locales/ta";
import kn from "../locales/kn";
import mr from "../locales/mr";

const LanguageContext = createContext(null);

const dictionaries = {
  en,
  hi,
  te,
  ur,
  ta,
  kn,
  mr,
};

function getNestedValue(obj, path) {
  return path.split(".").reduce((acc, key) => acc?.[key], obj);
}

export function LanguageProvider({ children }) {
  const { profile, user } = useAuth();
  const [language, setLanguageState] = useState(
    localStorage.getItem("mistribazaarLanguage") || "en"
  );

  useEffect(() => {
    if (profile?.preferred_language) {
      setLanguageState(profile.preferred_language);
      localStorage.setItem(
        "mistribazaarLanguage",
        profile.preferred_language
      );
    }
  }, [profile?.preferred_language]);

  const setLanguage = useCallback(
    async (newLanguage) => {
      setLanguageState(newLanguage);
      localStorage.setItem("mistribazaarLanguage", newLanguage);

      if (user?.id) {
        try {
          const { error } = await supabase
            .from("profiles")
            .update({ preferred_language: newLanguage })
            .eq("id", user.id);

          if (error) {
            console.error("Language save error:", error);
          }
        } catch (error) {
          console.error("Language save failed:", error);
        }
      }
    },
    [user?.id]
  );

  const t = useCallback(
    (key) => {
      const dictionary = dictionaries[language] || dictionaries.en;
      return (
        getNestedValue(dictionary, key) ||
        getNestedValue(dictionaries.en, key) ||
        key
      );
    },
    [language]
  );

  const availableLanguages = useMemo(
    () => [
      { code: "en", label: "English" },
      { code: "hi", label: "हिंदी" },
      { code: "te", label: "తెలుగు" },
      { code: "ur", label: "اردو" },
      { code: "ta", label: "தமிழ்" },
      { code: "kn", label: "ಕನ್ನಡ" },
      { code: "mr", label: "मराठी" },
    ],
    []
  );

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
      availableLanguages,
    }),
    [language, setLanguage, t, availableLanguages]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const value = useContext(LanguageContext);
  if (!value) {
    throw new Error("useLanguage must be used inside LanguageProvider");
  }
  return value;
}